import {createTheme} from '@mui/material/styles';

export const demoTheme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    colorSchemes: {light: true},
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    palette: {
        primary: {
            main: '#1B2A4A',
            light: '#2D4373',
            dark: '#0F1B33',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#4CAF82',
            light: '#6BC49A',
            dark: '#357A5B',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#F5F7FA',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1A1A2E',
            secondary: '#6B7280',
        },
        divider: '#E5E7EB',
        error: {
            main: '#EF4444',
        },
        warning: {
            main: '#F59E0B',
        },
        success: {
            main: '#10B981',
        },
        info: {
            main: '#3B82F6',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 700,
            letterSpacing: '-0.02em',
        },
        h5: {
            fontWeight: 700,
            letterSpacing: '-0.02em',
        },
        h6: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        subtitle1: {
            fontWeight: 500,
        },
        body1: {
            fontSize: '0.9375rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.8125rem',
            color: '#6B7280',
        },
        button: {
            textTransform: 'none' as const,
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 10,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#F5F7FA',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    height: '40px',
                    minHeight: '40px',
                    borderRadius: '8px',
                    textTransform: 'none' as const,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                        color: 'inherit',
                    },
                    '& .MuiSvgIcon-root': {
                        color: 'inherit',
                    },
                    '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    },
                    '&.Mui-disabled': {
                        color: '#9CA3AF',
                        backgroundColor: '#E5E7EB',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #1B2A4A 0%, #2D4373 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #0F1B33 0%, #1B2A4A 100%)',
                        boxShadow: '0 4px 12px rgba(27,42,74,0.3)',
                    },
                },
                containedSecondary: {
                    background: 'linear-gradient(135deg, #4CAF82 0%, #6BC49A 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #357A5B 0%, #4CAF82 100%)',
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: 'rgba(27,42,74,0.04)',
                    },
                },
            },
            defaultProps: {
                disableElevation: true,
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        height: '44px',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1B2A4A',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: '#1B2A4A',
                        },
                        '&.MuiInputBase-multiline': {
                            height: 'auto',
                        },
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: '10px 14px',
                        height: 'auto',
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    height: '44px',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                },
                select: {
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    height: 'auto',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    height: '44px',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    '&.MuiInputBase-multiline': {
                        height: 'auto',
                    },
                },
                notchedOutline: {
                    borderColor: '#E5E7EB',
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    '& .MuiTableRow-root': {
                        height: '48px',
                        maxHeight: '48px',
                    },
                    '& .MuiTableCell-root': {
                        height: '48px',
                        maxHeight: '48px',
                        padding: '8px 16px',
                    },
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        height: '48px',
                        maxHeight: '48px',
                    },
                    '& .MuiTableCell-root': {
                        height: '48px',
                        maxHeight: '48px',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.05em',
                        color: '#6B7280',
                        backgroundColor: '#F9FAFB',
                        borderBottom: '2px solid #E5E7EB',
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root': {
                        height: '48px',
                        maxHeight: '48px',
                        transition: 'background-color 0.15s ease-in-out',
                        '&:nth-of-type(even)': {
                            backgroundColor: '#F9FAFB',
                        },
                        '&:hover': {
                            backgroundColor: '#F0F2F5',
                        },
                    },
                    '& .MuiTableCell-root': {
                        height: '48px',
                        maxHeight: '48px',
                        padding: '8px 16px',
                        borderBottom: '1px solid #F3F4F6',
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: '48px',
                    maxHeight: '48px',
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '8px 16px',
                    height: '48px',
                    maxHeight: '48px',
                    lineHeight: '1.4',
                    fontSize: '0.875rem',
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        height: '44px',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        '&.MuiInputBase-multiline': {
                            height: 'auto',
                        },
                    },
                    '& .MuiInputBase-input': {
                        padding: '10px 14px',
                        height: 'auto',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    height: '44px',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    '&.MuiInputBase-multiline': {
                        height: 'auto',
                    },
                },
                input: {
                    padding: '10px 14px',
                    height: 'auto',
                    boxSizing: 'border-box',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                outlined: {
                    transform: 'translate(14px, 11px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    padding: '20px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: '1px solid #F3F4F6',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1B2A4A',
                    color: '#FFFFFF',
                    borderRight: 'none',
                    borderRadius: '0px',
                    '& .MuiSvgIcon-root': {
                        color: '#FFFFFF !important',
                        fill: '#FFFFFF !important',
                    },
                    '& .MuiListItemIcon-root': {
                        color: '#FFFFFF !important',
                    },
                    '& .MuiListItemText-root': {
                        color: '#FFFFFF !important',
                    },
                    '& .MuiListItemText-primary': {
                        color: '#FFFFFF !important',
                    },
                    '& .MuiTypography-root': {
                        color: '#FFFFFF !important',
                    },
                    '& .MuiCollapse-root': {
                        backgroundColor: '#1B2A4A',
                        '& .MuiSvgIcon-root': {
                            color: '#FFFFFF !important',
                            fill: '#FFFFFF !important',
                        },
                        '& .MuiListItemText-primary': {
                            color: '#FFFFFF !important',
                        },
                        '& .MuiTypography-root': {
                            color: '#FFFFFF !important',
                        },
                    },
                    '& .MuiList-root': {
                        backgroundColor: '#1B2A4A',
                    },
                    '& .MuiListSubheader-root': {
                        backgroundColor: '#1B2A4A',
                        color: '#FFFFFF !important',
                    },
                    '& .MuiDivider-root': {
                        borderColor: 'rgba(255,255,255,0.12)',
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    margin: '2px 8px',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(76, 175, 130, 0.2)',
                        borderLeft: '3px solid #4CAF82',
                        '&:hover': {
                            backgroundColor: 'rgba(76, 175, 130, 0.25)',
                        },
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: '#1A1A2E',
                    minWidth: '40px',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1A1A2E',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#E5E7EB',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#1A1A2E',
                    '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.35)',
                    },
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    fontWeight: 500,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    color: '#1A1A2E',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '56px',
                },
            },
        },
    },
});
